import { VisualizedResidue } from "./types";
import { useCallback, useEffect } from "react";
import { MolstarWrapper } from "@loschmidt/molstar";
import { setStructureOverpaint } from 'molstar/lib/mol-plugin-state/helpers/structure-overpaint';
import { StructureComponentManager } from 'molstar/lib/mol-plugin-state/manager/structure/component';
import { StructureRef } from 'molstar/lib/mol-plugin-state/manager/structure/hierarchy-state';
import { ParamDefinition } from 'molstar/lib/mol-util/param-definition';
import { PluginCommands } from 'molstar/lib/mol-plugin/commands';
import { lock, normalizeColor } from './utils';
import { isEmptyLoci, Loci } from 'molstar/lib/mol-model/loci';
import { groupBy } from "../utils/tools";
import { createResidueLociGetter } from "./residue-loci-getter";

export function useVisualizedResidues(
  viewer: MolstarWrapper | undefined,
  proteinModel: string | undefined,
  residues: VisualizedResidue[] | undefined,
) {

  const withStructureData = useCallback(async (callback: (structureData: StructureRef) => Promise<void>) => {
    // Adding side chains changes version of the structure, so we need to use always the current one.
    const structureData =
      viewer?.plugin.managers.structure.hierarchy.current.structures.find(
        s => s.cell.sourceRef === proteinModel
      );
    if (!structureData) return;
    await callback(structureData);
  }, [viewer, proteinModel]);

  useEffect(() => {
    if (!viewer || !proteinModel || !residues) return;

    let cancelled = false;
    let processedResidues: VisualizedResidue[] = [];

    const groups = Object.values(groupBy(residues, r => `${r.chain}-${r.color}`));

    (async () => {
      for (const group of groups) {
        const normColor = group[0].color ? normalizeColor(group[0].color) : -1;
        await withResiduesLock(async () => {
          await withStructureData(async structureData => {
            await setStructureOverpaint(
              viewer.plugin,
              structureData.components,
              normColor ?? -1,
              createResidueLociGetter(group[0].chain, group.map(r => r.position))
            );
          });

          const residuesWithSideChain = group.filter(r => r.sideChain);
          if (residuesWithSideChain.length > 0) {
            await withStructureData(async structureData => {
              const lociGetter = createResidueLociGetter(residuesWithSideChain[0].chain, residuesWithSideChain.map(r => r.position));
              const loci = await lociGetter(structureData.cell.obj!.data);
              await _addSideChains(viewer, loci);
            });
          }

          processedResidues.push(...group);
        });
        if (cancelled) break;
      }
    })();

    return () => {
      cancelled = true;
      // noinspection JSIgnoredPromiseFromCall
      withResiduesLock(async () => {
        const groups = Object.values(groupBy(processedResidues, r => r.chain));
        for (const group of groups) {
          await withStructureData(async structureData => {
            await setStructureOverpaint(
              viewer.plugin,
              structureData.components,
              -1,
              createResidueLociGetter(group[0].chain, group.map(r => r.position))
            )
          });
        }
        await withStructureData(async structureData => {
          await _removeAllSideChains(viewer, structureData);
        });
      });
    }

  }, [viewer, withStructureData, residues]);

}

function withResiduesLock(fn: () => Promise<void>): Promise<void> {
  return lock.acquire('RESIDUES', fn);
}

async function _addSideChains(viewer: MolstarWrapper, loci: Loci) {
  if (isEmptyLoci(loci)) return;

  // fromLoci() seems to not deselect manually selected residues of hidden proteins,
  // therefore, we have to use clear() before
  viewer.plugin.managers.structure.selection.clear();
  viewer.plugin.managers.structure.selection.fromLoci('set', loci);

  const repr = 'ball-and-stick';
  const defaultParams = StructureComponentManager.getAddParams(viewer.plugin, {
    allowNone: false,
    hideSelection: true,
    checkExisting: false,
  });
  const defaultValues = ParamDefinition.getDefaultValues(defaultParams);
  const values = {
    ...defaultValues,
    options: { label: 'selection-by-script', checkExisting: false },
    representation: repr,
  };
  const structures =
    viewer.plugin.managers.structure.hierarchy.getStructuresWithSelection();
  await viewer.plugin.managers.structure.component.add(values, structures);

  viewer.plugin.managers.structure.selection.clear();
}

async function _removeAllSideChains(viewer: MolstarWrapper, structureData: StructureRef) {
  for (const {cell} of structureData.components) {
    if (cell?.params?.values?.label === 'selection-by-script') {
      await PluginCommands.State.RemoveObject(viewer.plugin, {
        state: viewer.plugin.state.data,
        ref: cell.transform.ref,
      });
    }
  }
}